const React = require('react')
const { useState, useEffect } = require('react')
const { useHistory } = require('react-router-dom')
const { Row, Col, Button, UncontrolledTooltip } = require('reactstrap')
const { useFormatBytes, useFormatTimestamp, useHexToString, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner, CopyButton } = require('@components')
const { ZERO_BYTES32 } = require('@constants')

require('@styles/pages/block/details.block.css')

const BlockDetails = props => {
    const { loading, data, latestBlock, blockTime, isDetailsExpanded, setIsDetailsExpanded } = props
    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestamp()
    const formatHexToString = useHexToString()
    const isMobile = useResponsive(BREAKPOINTS.small)

    const [blockDetails, setBlockDetails] = useState([])
    const [blockDetailsExpanded, setBlockDetailsExpanded] = useState([])

    const isBlockchain_1 = network.data.type === 'Ethereum 1.0'
    const isBlockchain_2 = network.data.type === 'Ethereum 2.0'

    useEffect(() => {
        if (!data || !latestBlock || blockTime === undefined) return

        const isPending = latestBlock - data.number <= 6
        const staticReward = 0
        const burntFees = data.gasUsed * data.baseFeePerGas

        const fields = {
            divider: {
                id: 'divider'
            },
            height: {
                id: 'block-height',
                label: 'Block Height',
                value: (
                    <div className='block-navigation'>
                        {data.number}
                        <div className='block-nav-buttons'>
                            <div className='block-nav-button-wrapper' id='prev-block'>
                                <Button
                                    className='block-nav-button'
                                    disabled={data.number === '0'}
                                    onClick={() => {
                                        const prevBlock = parseInt(data.number) - 1
                                        if (prevBlock >= 0) {
                                            history.push(`/${network.name}/block/${prevBlock}`)
                                        }
                                    }}
                                >
                                    <i className='fas fa-chevron-left' />
                                </Button>
                                <UncontrolledTooltip placement='top' target='prev-block'>
                                    {data.number === '0'
                                        ? 'You have reached the earliest block'
                                        : 'View previous block'}
                                </UncontrolledTooltip>
                            </div>
                            <div className='block-nav-button-wrapper' id='next-block'>
                                <Button
                                    className='block-nav-button'
                                    disabled={parseInt(data.number) >= latestBlock}
                                    onClick={() => {
                                        const nextBlock = parseInt(data.number) + 1
                                        history.push(`/${network.name}/block/${nextBlock}`)
                                    }}
                                >
                                    <i className='fas fa-chevron-right' />
                                </Button>
                                <UncontrolledTooltip placement='top' target='next-block'>
                                    {parseInt(data.number) >= latestBlock
                                        ? 'You have reached the latest block'
                                        : 'View next block'}
                                </UncontrolledTooltip>
                            </div>
                        </div>
                    </div>
                ),
                tooltip:
                    'Also known as Block Number. The block height, which indicates the length of the blockchain, increases after the addition of the new block.'
            },
            status: {
                id: 'block-status',
                label: 'Status',
                value: (
                    <>
                        <div
                            id='block-status-value'
                            className={`status-box ${isPending ? 'pending' : 'success'} tx-status-indicator`}
                        >
                            <i
                                className={`fas ${isPending ? 'fa-hourglass-start' : 'fa-check'}`}
                                style={{
                                    color: `${isPending ? 'var(--pending-color)' : 'var(--white-color)'}`,
                                    fontSize: '10px',
                                    backgroundColor: `${isPending ? 'transparent' : 'var(--success-color)'}`,
                                    padding: '8px'
                                }}
                            />
                            {isPending ? 'Unfinalized' : 'Finalized'}
                        </div>
                        <UncontrolledTooltip placement='top' target='block-status-value'>
                            {isPending
                                ? 'This block is unfinalized and may be susceptible to reorgs.'
                                : 'This block is finalized and cannot be reverted.'}
                        </UncontrolledTooltip>
                    </>
                ),
                tooltip: 'The finality status of the block.'
            },
            timestamp: {
                id: 'block-timestamp',
                label: 'Timestamp',
                value: (
                    <>
                        <i className='fas fa-clock' style={{ fontSize: '12px' }} />
                        &nbsp;
                        {formatTimestamp(data.timestamp)}
                    </>
                ),
                tooltip: 'The date and time at which a block is produced.'
            },
            proposedOn: {
                id: 'block-proposed-on',
                label: 'Proposed On',
                value: (
                    <>
                        Block proposed on slot{' '}
                        <span
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => {
                                history.push(`/${network.name}/slot/${data.beacon.slot}`)
                            }}
                        >
                            {data.beacon.slot}
                        </span>
                        , epoch{' '}
                        <span
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => {
                                history.push(`/${network.name}/epoch/${data.beacon.epoch}`)
                            }}
                        >
                            {data.beacon.epoch}
                        </span>
                    </>
                ),
                tooltip: 'Slot and epoch this block is proposed on.'
            },
            transactions: {
                id: 'block-transactions',
                label: 'Transactions',
                value: (
                    <>
                        {data.transactionCount > 0 ? (
                            <span
                                id='block-total-transactions'
                                className={`${network.name}-text-color cursor-pointer`}
                                onClick={() => {
                                    history.push(`/${network.name}/txs?block=${data.number}`)
                                }}
                            >
                                {data.transactionCount}
                                &nbsp;
                                {data.transactionCount === 1 ? 'transaction' : 'transactions'}
                            </span>
                        ) : (
                            <span>
                                {data.transactionCount}
                                &nbsp; transaction
                            </span>
                        )}
                        &nbsp;and&nbsp;
                        {data.internalTransactionCount > 0 ? (
                            <span
                                id='block-total-internal-transactions'
                                className={`${network.name}-text-color cursor-pointer`}
                                onClick={() => {
                                    history.push(`/${network.name}/txsInternal?block=${data.number}`)
                                }}
                            >
                                {data.internalTransactionCount}
                                &nbsp;
                                {data.internalTransactionCount === 1
                                    ? 'contract internal transaction'
                                    : 'contract internal transactions'}
                            </span>
                        ) : (
                            <span>
                                {data.internalTransactionCount}
                                &nbsp; contract internal transaction
                            </span>
                        )}
                        &nbsp; in this block
                        {data.transactionCount > 0 && (
                            <>
                                <UncontrolledTooltip placement='top' target='block-total-transactions'>
                                    Click to view Transactions
                                </UncontrolledTooltip>
                            </>
                        )}
                        {data.internalTransactionCount > 0 && (
                            <UncontrolledTooltip placement='top' target='block-total-internal-transactions'>
                                Click to view Internal Transactions
                            </UncontrolledTooltip>
                        )}
                    </>
                ),
                tooltip:
                    'The number of transactions in the block. Internal transacion is transactions as a result of contract execution that involves value.'
            },
            withdrawals: {
                id: 'block-withdrawals',
                label: 'Withdrawals',
                value: (
                    <>
                        {data.withdrawals > 0 ? (
                            <span
                                className={`${network.name}-text-color cursor-pointer`}
                                onClick={() => {
                                    history.push(`/${network.name}/txsBeaconWithdrawal?block=${data.number}`)
                                }}
                            >
                                {data?.withdrawals}
                                &nbsp;withdrawal
                                {data?.withdrawals === 1 ? '' : 's'}
                            </span>
                        ) : (
                            <span>
                                {data?.withdrawals}
                                &nbsp; withdrawal
                            </span>
                        )}
                        &nbsp; in this block
                    </>
                ),
                tooltip: 'Number of beacon withdrawals in this block.'
            },
            miner: {
                id: 'block-miner',
                label: 'Miner',
                value: (
                    <>
                        <span
                            id='block-miner-address'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => {
                                history.push(`/${network.name}/address/${data.validator}`)
                            }}
                        >
                            {formatBytes(data.validator, 50, 42, 0)}
                        </span>
                        <UncontrolledTooltip placement='top' target='block-miner-address'>
                            {data.validator}
                        </UncontrolledTooltip>
                        &nbsp;
                        <CopyButton id={`copy-miner-${data.validator}`} data={data.validator} text={'Copy address'} />
                        &nbsp;in {blockTime} secs
                    </>
                ),
                tooltip: 'Miner who successfully include the block onto the blockchain.'
            },
            feeRecipient: {
                id: 'block-fee-recipient',
                label: 'Fee Recipient',
                value: (
                    <>
                        <span
                            id='block-fee-recipient-address'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => {
                                history.push(`/${network.name}/address/${data.validator}`)
                            }}
                        >
                            {formatBytes(data.validator, 50, 42, 0)}
                        </span>
                        <UncontrolledTooltip placement='top' target='block-fee-recipient-address'>
                            {data.validator}
                        </UncontrolledTooltip>
                        &nbsp;
                        <CopyButton
                            id={`copy-fee-recipient-${data.validator}`}
                            data={data.validator}
                            text={'Copy address'}
                        />
                        &nbsp;in {blockTime} secs
                    </>
                ),
                tooltip: 'Address receiving fees from transactions in this block.'
            },
            reward: {
                id: 'block-reward',
                label: 'Block Reward',
                value: (
                    <>
                        {((staticReward + data.totalTransactionFees) / 10 ** 18).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 18
                        })}
                        &nbsp;{network.data.token} (<span id='block-reward-static'>{staticReward}</span>
                        <UncontrolledTooltip placement='top' target='block-reward-static'>
                            Static Block Reward
                        </UncontrolledTooltip>
                        &nbsp;+
                        <span id='block-reward-tx-fee'>
                            &nbsp;
                            {(data.totalTransactionFees / 10 ** 18).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 18
                            })}
                        </span>
                        <UncontrolledTooltip placement='top' target='block-reward-tx-fee'>
                            Transaction Fees
                        </UncontrolledTooltip>
                        )
                    </>
                ),
                tooltip: `For each block, the miner is reward with a finite amount of ${network.data.token} on top of the fees paid for all transactions in the block.`
            },
            reward_2: {
                id: 'block-reward',
                label: 'Block Reward',
                value: (
                    <>
                        {((staticReward + data.totalTransactionFees - burntFees) / 10 ** 18).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 18
                        })}
                        &nbsp;{network.data.token} (<span id='block-reward-static'>{staticReward}</span>
                        <UncontrolledTooltip placement='top' target='block-reward-static'>
                            Static Block Reward
                        </UncontrolledTooltip>
                        &nbsp;+
                        <span id='block-reward-tx-fee'>
                            &nbsp;
                            {(data.totalTransactionFees / 10 ** 18).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 18
                            })}
                        </span>
                        <UncontrolledTooltip placement='top' target='block-reward-tx-fee'>
                            Transaction Fees
                        </UncontrolledTooltip>
                        &nbsp;-
                        <span id='block-reward-tx-burnt-fees'>
                            &nbsp;
                            {(burntFees / 10 ** 18).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 18
                            })}
                        </span>
                        <UncontrolledTooltip placement='top' target='block-reward-tx-burnt-fees'>
                            Burnt Fees
                        </UncontrolledTooltip>
                        )
                    </>
                ),
                tooltip: `For each block, the producer is rewarded with a finite amount of ${network.data.token} on top of the fees paid for all transactions in the block.`
            },
            unclesReward: {
                id: 'block-uncles-reward',
                label: 'Uncles Reward',
                value: `0 ${network.data.token}`,
                tooltip:
                    'An uncle block has a significantly lower reward than a normal block. Unlce rewards is valid but rejected as it not on the longest chain which is the working mechanism of the blockchain. Uncle block is important in blockchain as it secures the blockchain.'
            },
            difficulty: {
                id: 'block-difficulty',
                label: 'Difficulty',
                value: Number(data?.difficulty).toLocaleString(),
                tooltip:
                    'The amount of effort require to mine a new block. The difficulty algorithm may adjust according to time.'
            },
            totalDifficulty: {
                id: 'block-total-difficulty',
                label: 'Total Difficulty',
                value: Number(data.totalDifficulty).toLocaleString(),
                tooltip: 'Total difficulty of the chain until this block.'
            },
            size: {
                id: 'block-size',
                label: 'Size',
                value: <>{data.size.toLocaleString()} bytes</>,
                tooltip: "The block size is actually determined by the block's gas limit."
            },
            gasUsed: {
                id: 'block-gas-used',
                label: 'Gas Used',
                value: (
                    <>
                        {data.gasUsed.toLocaleString()}
                        &nbsp;
                        <span style={{ color: 'var(--grey-color)' }}>
                            (
                            {((data.gasUsed / data.gasLimit) * 100).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2
                            })}
                            %)
                        </span>
                    </>
                ),
                tooltip: 'The total gas used in the block and its percentage of gass filled in the block.'
            },
            gasLimit: {
                id: 'block-gas-limit',
                label: 'Gas Limit',
                value: data.gasLimit.toLocaleString(),
                tooltip: 'Total gas limit provided by all transactions in the block.'
            },
            baseFeePerGas: {
                id: 'block-base-fee-per-gas',
                label: 'Base Fee Per Gas',
                value: (
                    <>
                        {(data.baseFeePerGas / 10 ** 18).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 18
                        })}
                        &nbsp;{network.data.token}&nbsp;
                        <span style={{ color: 'var(--grey-color)' }}>
                            (
                            {(data.baseFeePerGas / 10 ** 9).toLocaleString(undefined, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 9
                            })}
                            &nbsp;Gwei)
                        </span>
                    </>
                ),
                tooltip:
                    'Post-London Upgrade, this represents the minimum gasUsed multiplier required for a tx to be included in a block.'
            },
            burntFees: {
                id: 'block-burnt-fees',
                label: 'Burnt Fees',
                value: (
                    <>
                        🔥&nbsp;
                        {(burntFees / 10 ** 18).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 18
                        })}
                        &nbsp;{network.data.token}
                    </>
                ),
                tooltip:
                    'Post-London Upgrade, this represents the part of the tx fee that is burnt: baseFeePerGas * gasUsed.'
            },
            extraData: {
                id: 'block-extra-data',
                label: 'Extra Data',
                value: (
                    <>
                        {formatHexToString(data.extraData)}
                        &nbsp;
                        <span id='block-extra-data-detail'>(Hex: {formatBytes(data.extraData, 60, 10, 10)})</span>
                        <UncontrolledTooltip placement='top' target='block-extra-data-detail'>
                            {data.extraData}
                        </UncontrolledTooltip>
                    </>
                ),
                tooltip: 'Any data that can be included by the miner in the block.'
            },
            extraData_2: {
                id: 'block-extra-data',
                label: 'Extra Data',
                value: (
                    <>
                        {formatHexToString(data.extraData)}
                        &nbsp;
                        <span id='block-extra-data-detail'>(Hex: {formatBytes(data.extraData, 60, 10, 10)})</span>
                        <UncontrolledTooltip placement='top' target='block-extra-data-detail'>
                            {data.extraData}
                        </UncontrolledTooltip>
                    </>
                ),
                tooltip: 'Any data that can be included by the producer in the block.'
            },
            hash: {
                id: 'block-hash',
                label: 'Hash',
                value: data.hash,
                tooltip: 'The hash of the block header of the current block.'
            },
            parentHash: {
                id: 'block-parent-hash',
                label: 'Parent Hash',
                value: (
                    <>
                        <span
                            className={`${
                                data.parentHash != ZERO_BYTES32 ? `${network.name}-text-color cursor-pointer` : ''
                            }`}
                            onClick={() => {
                                if (data.parentHash != ZERO_BYTES32) {
                                    history.push(`/${network.name}/block/${data.number - 1}`)
                                }
                            }}
                        >
                            {data.parentHash}
                        </span>
                    </>
                ),
                tooltip: 'The hash of the block which this block in generated, also known as its parent block.'
            },
            sha3Uncles: {
                id: 'block-sha3-uncles',
                label: 'Sha3Uncles',
                value: data.sha3Uncles,
                tooltip: 'The mechanism which blockchain Javascript RLP encodes an empty string.'
            },
            stateRoot: {
                id: 'block-state-root',
                label: 'StateRoot',
                value: data.stateRoot,
                tooltip: 'The root of the state trie.'
            },
            nonce: {
                id: 'block-nonce',
                label: 'Nonce',
                value: data.nonce,
                tooltip: 'Block nonce is value used during mining to demonstrate proof of work for a block.'
            }
        }

        const blockOrder_blockchain_1 = [
            'height',
            'status',
            'timestamp',
            'transactions',
            'divider',
            'miner',
            'reward',
            'unclesReward',
            'difficulty',
            'totalDifficulty',
            'size',
            'divider',
            'gasUsed',
            'gasLimit',
            'extraData'
        ]

        const blockOrder_blockchain_2 = [
            'height',
            'status',
            'timestamp',
            'proposedOn',
            'transactions',
            'withdrawals',
            'divider',
            'feeRecipient',
            'reward_2',
            'totalDifficulty',
            'size',
            'divider',
            'gasUsed',
            'gasLimit',
            'baseFeePerGas',
            'burntFees',
            'extraData_2'
        ]

        const blockDetailsOrder = isBlockchain_1 ? blockOrder_blockchain_1 : blockOrder_blockchain_2
        setBlockDetails(blockDetailsOrder.map(key => fields[key]))

        const blockDetailsExpandedOrder = ['hash', 'parentHash', 'stateRoot', 'nonce', 'divider']
        setBlockDetailsExpanded(blockDetailsExpandedOrder.map(key => fields[key]))
    }, [data, latestBlock, blockTime])

    return (
        <>
            {loading ? (
                <div className='box table-section'>
                    <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            ) : (
                <>
                    <div className='box table-section'>
                        {blockDetails.map(item => (
                            <Row className='table-info-row' noGutters>
                                {item.id === 'divider' ? (
                                    <div className='table-divider' />
                                ) : (
                                    <>
                                        <Col xs={12} sm={4} className='d-flex align-items-start'>
                                            <span className='info-icon-wrapper' id={item.id}>
                                                <i className='fas fa-question-circle info-icon' />
                                            </span>
                                            <span className='info-label'>{item.label}:</span>
                                        </Col>
                                        <UncontrolledTooltip placement='top' target={item.id}>
                                            {item.tooltip}
                                        </UncontrolledTooltip>
                                        <Col
                                            xs={12}
                                            sm={8}
                                            className={`d-flex align-items-center ${isMobile && 'mt-2'}`}
                                            style={{ flexWrap: 'wrap', wordBreak: 'break-all' }}
                                        >
                                            <span className='info-value' style={{ width: '100%' }}>
                                                {item.value}
                                            </span>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        ))}
                    </div>
                    <div className='box table-section'>
                        {isDetailsExpanded && (
                            <>
                                {blockDetailsExpanded.map(item => (
                                    <Row className='table-info-row' noGutters>
                                        {item.id === 'divider' ? (
                                            <div className='table-divider' />
                                        ) : (
                                            <>
                                                <Col xs={12} sm={4} className='d-flex align-items-center'>
                                                    <span className='info-icon-wrapper' id={item.id}>
                                                        <i className='fas fa-question-circle info-icon' />
                                                    </span>
                                                    <span className='info-label'>{item.label}:</span>
                                                </Col>
                                                <UncontrolledTooltip placement='top' target={item.id}>
                                                    {item.tooltip}
                                                </UncontrolledTooltip>
                                                <Col
                                                    xs={12}
                                                    sm={8}
                                                    className={`d-flex align-items-center ${isMobile && 'mt-2'}`}
                                                    style={{ flexWrap: 'wrap', wordBreak: 'break-all' }}
                                                >
                                                    <span className='info-value' style={{ width: '100%' }}>
                                                        {item.value}
                                                    </span>
                                                </Col>
                                            </>
                                        )}
                                    </Row>
                                ))}
                            </>
                        )}
                        <Row
                            className='table-info-row'
                            noGutters
                            style={{ cursor: 'pointer' }}
                            onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
                        >
                            <Col xs={12} sm={4} className='d-flex align-items-center'>
                                <span className='info-label'>More Details:</span>
                            </Col>
                            <Col xs={12} sm={8} className={`d-flex align-items-center ${isMobile && 'mt-2'}`}>
                                <span className={`info-value ${network.name}-text-color`}>
                                    {isDetailsExpanded ? '- Click to show less' : '+ Click to show more'}
                                </span>
                            </Col>
                        </Row>
                    </div>
                </>
            )}
        </>
    )
}

module.exports = { BlockDetails }
